// "Add digital employee" (DE-WIZARD): the channels a DE may join, for the create flow and the
// edit mode, and (edit mode) the ones it has joined.
// - Labels (KPI-FIX's, as the old deploy wizard had them): "#operations", or "#operations ·
//   Northwind Ops" when two listed channels share a name; the spaces are read only then.
// - Joined: there is no "channels of a member" route, so each listed channel's members
//   (`channels.members`, the cache Chat's Members dialog shares) are read, only where asked.
import { useQueries, useQuery } from "@tanstack/react-query";
import type { Api } from "../../../api/client";
import { qualifiedChannelName, spaceQualifiers } from "../../chat/chat-model";
import { channelsQuery, membersQuery, spacesQuery } from "../../chat/chat-queries";
import type { JoinableChannel } from "./steps";

/** The channels a DE may join (no DMs, agent rooms or archived ones), labelled; null: Chat off. */
export function useJoinableChannels(api: Api, co: string, chatOn: boolean) {
  const channels = useQuery({ ...channelsQuery(api, co), enabled: chatOn, retry: false });
  const listed = (channels.data?.data ?? []).filter(
    (c) => !c.dm_key && !c.archived_at && !c.is_agent_room,
  );
  const shared = new Set(listed.map((c) => c.name)).size < listed.length;
  const spaces = useQuery({ ...spacesQuery(api, co), enabled: chatOn && shared, retry: false });
  const qualifiers = spaceQualifiers(listed, spaces.data?.data ?? []);
  const joinable: JoinableChannel[] = listed.map((c) => ({
    id: c.id,
    name: c.name,
    label: qualifiedChannelName(c, qualifiers),
  }));
  return { joinable, list: chatOn && channels.isSuccess ? joinable : null };
}

/** The listed channels this member belongs to; null while their members load (or Chat is off). */
export function useJoinedChannels(
  api: Api,
  co: string,
  membershipId: string,
  channels: JoinableChannel[] | null,
): JoinableChannel[] | null {
  const members = useQueries({
    queries: (channels ?? []).map((c) => ({ ...membersQuery(api, co, c.id), retry: false })),
  });
  if (!channels || members.some((m) => m.isPending)) return null;
  return channels.filter((_, i) =>
    members[i]?.data?.data.some((m) => m.membership_id === membershipId),
  );
}

/** "#a, #b · Ops" or "None". */
export const channelNames = (list: readonly JoinableChannel[]) =>
  list.map((c) => c.label).join(", ") || "None";
